import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  AppSettings,
  CalibrationProfile,
  EnvironmentSnapshot,
  ModuleSummary,
  SessionCompletionPayload,
  SessionRecord,
  TrialResult,
} from '../core/types';
import { importAllJson, repository } from '../data';

interface AppStateCache {
  settings: AppSettings;
  calibration?: CalibrationProfile;
  environment?: EnvironmentSnapshot;
  sessions: SessionRecord[];
  summariesBySession: Record<string, ModuleSummary[]>;
}

interface AppContextValue extends AppStateCache {
  ready: boolean;
  loadError?: string;
  lastCompletion?: SessionCompletionPayload;
  saveCalibration: (calibration: CalibrationProfile, environment: EnvironmentSnapshot) => Promise<void>;
  activateEnvironment: (environment: EnvironmentSnapshot) => Promise<void>;
  activateStoredEnvironment: (environmentId: string) => Promise<void>;
  saveCompletion: (payload: SessionCompletionPayload) => Promise<void>;
  saveSessionStart: (session: SessionRecord) => Promise<void>;
  saveTrial: (trial: TrialResult) => Promise<void>;
  saveAbort: (session: SessionRecord, trials: TrialResult[]) => Promise<void>;
  loadSessionTrials: (sessionId: string) => Promise<TrialResult[]>;
  updateSettings: (patch: Partial<AppSettings>) => Promise<void>;
  importBackup: (json: string) => Promise<void>;
  refresh: () => Promise<void>;
  clearAll: () => Promise<void>;
}

const defaultSettings: AppSettings = {
  profileName: 'PLAYER 01',
  age: 34,
  position: 'field',
  onboardingComplete: false,
  preferredKeys: { choice1: '1', choice2: '2', choice3: '3', choice4: '4', pause: 'Escape' },
};

const defaultState: AppStateCache = {
  settings: defaultSettings,
  sessions: [],
  summariesBySession: {},
};

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppStateCache>(defaultState);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string>();
  const [lastCompletion, setLastCompletion] = useState<SessionCompletionPayload>();

  const refresh = useCallback(async () => {
    setReady(false);
    setLoadError(undefined);
    try {
      const [settings, sessions] = await Promise.all([repository.getSettings(), repository.listSessions()]);
      const calibration = settings.calibrationId
        ? await repository.getCalibration(settings.calibrationId)
        : await repository.getLatestCalibration();
      const environment = calibration ? await repository.getLatestEnvironment(calibration.id) : undefined;
      const summaries = await Promise.all(
        sessions.map(async (session) => [session.id, await repository.getSessionSummaries(session.id)] as const),
      );
      const summariesBySession = Object.fromEntries(summaries);
      setState({ settings, calibration, environment, sessions, summariesBySession });
      setReady(true);
    } catch (error) {
      setLoadError('端末内データを読み込めませんでした。データを空として扱わず停止しています。');
      setReady(true);
      throw error;
    }
  }, []);

  useEffect(() => {
    void refresh().catch(() => undefined);
  }, [refresh]);

  const saveCalibration = useCallback(async (calibration: CalibrationProfile, environment: EnvironmentSnapshot) => {
    const settings = await repository.saveCalibrationBundle(calibration, environment);
    setState((current) => ({
      ...current,
      calibration,
      environment,
      settings,
    }));
  }, []);

  const activateEnvironment = useCallback(async (environment: EnvironmentSnapshot) => {
    const saved = await repository.saveEnvironment(environment);
    setState((current) => ({ ...current, environment: saved }));
  }, []);

  const activateStoredEnvironment = useCallback(async (environmentId: string) => {
    const environment = await repository.getEnvironment(environmentId);
    if (!environment) throw new Error(`Missing environment snapshot ${environmentId}`);
    setState((current) => ({ ...current, environment }));
  }, []);

  const saveCompletion = useCallback(async (payload: SessionCompletionPayload) => {
    const storedSession = await repository.saveSessionCompletion(payload);
    setLastCompletion(payload);
    setState((current) => {
      const sessions = [storedSession, ...current.sessions.filter((item) => item.id !== storedSession.id)];
      const summariesBySession = { ...current.summariesBySession, [payload.session.id]: payload.summaries };
      return { ...current, sessions, summariesBySession };
    });
  }, []);

  const saveSessionStart = useCallback(async (session: SessionRecord) => {
    const stored = await repository.saveSession({ ...session, status: 'in-progress', completedAt: undefined });
    setState((current) => ({
      ...current,
      sessions: [stored, ...current.sessions.filter((item) => item.id !== stored.id)],
    }));
  }, []);

  const saveTrial = useCallback(async (trial: TrialResult) => {
    await repository.addTrial(trial);
    setState((current) => ({
      ...current,
      sessions: current.sessions.map((session) => session.id === trial.sessionId && !session.trialIds.includes(trial.id)
        ? { ...session, trialIds: [...session.trialIds, trial.id] }
        : session),
    }));
  }, []);

  const saveAbort = useCallback(async (session: SessionRecord, trials: TrialResult[]) => {
    const stored = await repository.saveAbortedSession(session, trials);
    setState((current) => ({
      ...current,
      sessions: [stored, ...current.sessions.filter((item) => item.id !== stored.id)],
    }));
  }, []);

  const loadSessionTrials = useCallback((sessionId: string) => repository.getSessionTrials(sessionId), []);

  const updateSettings = useCallback(async (patch: Partial<AppSettings>) => {
    const settings = await repository.saveSettings(patch);
    setState((current) => ({ ...current, settings }));
  }, []);

  const importBackup = useCallback(async (json: string) => {
    await importAllJson(json, { replace: true });
    await refresh();
  }, [refresh]);

  const clearAll = useCallback(async () => {
    await repository.clearAll();
    setLastCompletion(undefined);
    setLoadError(undefined);
    setState(defaultState);
  }, []);

  const value = useMemo<AppContextValue>(() => ({
    ...state,
    ready,
    loadError,
    lastCompletion,
    saveCalibration,
    activateEnvironment,
    activateStoredEnvironment,
    saveCompletion,
    saveSessionStart,
    saveTrial,
    saveAbort,
    loadSessionTrials,
    updateSettings,
    importBackup,
    refresh,
    clearAll,
  }), [
    state,
    ready,
    loadError,
    lastCompletion,
    saveCalibration,
    activateEnvironment,
    activateStoredEnvironment,
    saveCompletion,
    saveSessionStart,
    saveTrial,
    saveAbort,
    loadSessionTrials,
    updateSettings,
    importBackup,
    refresh,
    clearAll,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error('useApp must be used inside AppProvider');
  return value;
}

export type { AppStateCache };
